// TEMPORARY FEATURE: drawn overlay images (see the frontend's mattress-inspector/components/overlay-drawing).
// To remove it without breaking saved graphs, keep this class registered under the same nodeClass and let evaluate() return null,
// or migrate the affected graphs first and delete the file afterwards.

import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {hashObject} from "@cm/utils"
import type {TypedImageData} from "@cm/utils"
import {ImageNode} from "#template/nodes/image/image-node"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {generateImageNodeForInMemoryImageData} from "#template/nodes/image/in-memory-image"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {ImageGenerator, ImageNodeGeneratorData} from "@cm/material-nodes"
import {TypeDescriptors as TD} from "#template/runtime-graph/type-descriptors"
import {ParametricCurve} from "#template/utils/parametric-curve"
import {Three as THREE} from "@cm/material-nodes/three"

export type CurveDrawingStroke = {
    // positions in cm, origin at the bottom left of the drawing area, y pointing up
    controlPoints: [number, number][]
}

export type CurveDrawingImageParameters = {
    name: string
    widthCm: number
    heightCm: number
    resolution: number // pixels along the longer side
    strokeWidthCm: number
    color: [number, number, number]
    strokes: CurveDrawingStroke[]
}

const curveDrawingImageDefinition: TemplateNodeDefinition<CurveDrawingImageParameters> = {
    nodeClass: "CurveDrawingImage",
    validation: {
        parameters: z.object({
            name: z.string(),
            widthCm: z.number().check(z.positive()),
            heightCm: z.number().check(z.positive()),
            resolution: z.number().check(z.positive()),
            strokeWidthCm: z.number().check(z.positive()),
            color: z.tuple([z.number(), z.number(), z.number()]),
            strokes: z.array(
                z.object({
                    controlPoints: z.array(z.tuple([z.number(), z.number()])),
                }),
            ),
        }),
        returns: getTemplateNodeReturns(),
    },
}

const curveDrawingImageParametersDesc = TD.Identity<CurveDrawingImageParameters>()

const maxResolution = 4096

// shared with the drawing dialog, so that its hit testing follows the rendered curve
export function createCurveDrawingCurve(controlPoints: [number, number][]) {
    return new ParametricCurve(
        controlPoints.map(([x, y]) => new THREE.Vector3(x, y, 0)),
        false,
        "centripetal",
        1,
    )
}

export function sampleCurveDrawingStroke(controlPoints: [number, number][], maxSegmentLength: number): [number, number][] {
    if (controlPoints.length < 2) return controlPoints.map(([x, y]) => [x, y])
    const curve = createCurveDrawingCurve(controlPoints)
    const divisions = Math.min(Math.max(1, Math.ceil(curve.getLength() / maxSegmentLength)), 100000)
    return curve.getSpacedPoints(divisions).map((point) => [point.x, point.y])
}

export function rasterizeCurveDrawing(parameters: Omit<CurveDrawingImageParameters, "name">): TypedImageData<Uint8ClampedArray> {
    const {widthCm, heightCm, strokeWidthCm, color, strokes} = parameters
    const resolution = Math.min(Math.max(1, Math.round(parameters.resolution)), maxResolution)
    const pxPerCm = resolution / Math.max(widthCm, heightCm)
    const width = Math.max(1, Math.round(widthCm * pxPerCm))
    const height = Math.max(1, Math.round(heightCm * pxPerCm))

    const coverage = new Float32Array(width * height)
    const radius = (strokeWidthCm * pxPerCm) / 2

    // pixel space: x to the right, y downwards (row 0 is the top of the image)
    const toPx = ([x, y]: [number, number]): [number, number] => [x * pxPerCm, (heightCm - y) * pxPerCm]

    const drawCapsule = ([ax, ay]: [number, number], [bx, by]: [number, number]) => {
        const minX = Math.max(0, Math.floor(Math.min(ax, bx) - radius - 1))
        const maxX = Math.min(width - 1, Math.ceil(Math.max(ax, bx) + radius + 1))
        const minY = Math.max(0, Math.floor(Math.min(ay, by) - radius - 1))
        const maxY = Math.min(height - 1, Math.ceil(Math.max(ay, by) + radius + 1))
        const dx = bx - ax
        const dy = by - ay
        const lengthSq = dx * dx + dy * dy

        for (let py = minY; py <= maxY; py++) {
            for (let px = minX; px <= maxX; px++) {
                const cx = px + 0.5 - ax
                const cy = py + 0.5 - ay
                const t = lengthSq > 0 ? Math.min(1, Math.max(0, (cx * dx + cy * dy) / lengthSq)) : 0
                const distance = Math.hypot(cx - t * dx, cy - t * dy)
                const value = Math.min(1, Math.max(0, radius + 0.5 - distance))
                const index = py * width + px
                if (value > coverage[index]) coverage[index] = value
            }
        }
    }

    for (const stroke of strokes) {
        const points = sampleCurveDrawingStroke(stroke.controlPoints, 2 / pxPerCm).map(toPx)
        if (points.length === 1) drawCapsule(points[0], points[0])
        for (let i = 1; i < points.length; i++) drawCapsule(points[i - 1], points[i])
    }

    const toByte = (value: number) => Math.round(Math.min(1, Math.max(0, value)) * 255)
    const [r, g, b] = color.map(toByte)
    const data = new Uint8ClampedArray(width * height * 4)
    for (let i = 0; i < coverage.length; i++) {
        data[i * 4] = r
        data[i * 4 + 1] = g
        data[i * 4 + 2] = b
        data[i * 4 + 3] = toByte(coverage[i])
    }

    return {data, width, height, channelLayout: "RGBA", dataType: "uint8", colorSpace: "sRGB"}
}

@registerTemplateGraphNode
export class CurveDrawingImage extends ImageNode<CurveDrawingImageParameters> {
    public override get definition() {
        return curveDrawingImageDefinition
    }

    public override evaluate(scope: GraphBuilderScope, _evaluator: NodeEvaluator): BuilderInlet<ImageGenerator | null> {
        return scope.pureLambda<CurveDrawingImageParameters, ImageGenerator>(
            scope.value(this.parameters, curveDrawingImageParametersDesc, "parameters"),
            ({name: _name, ...parameters}) => {
                const imageData = rasterizeCurveDrawing(parameters)
                return {
                    imageNode: {
                        generator: (imageGeneratorData: ImageNodeGeneratorData) => generateImageNodeForInMemoryImageData(imageData, imageGeneratorData),
                        hash: hashObject({type: "CurveDrawingImage", ...parameters}),
                    },
                    metadata: {width: imageData.width, height: imageData.height},
                }
            },
            "result",
        )
    }
}
